import type { FilterFieldConfig } from '../config/filterable-fields';
import type { CustomFieldDefinition } from '../types/custom-field-definitions.types';
import { getAttributeTypeLabel } from './attribute-definition-mapper';

export type CustomFieldTier = 'native' | 'default' | 'custom';

export type CustomFieldTableRow = {
  key: string;
  itemId?: string;
  label: string;
  dataTypeLabel: string;
  isFilterable: boolean;
  choices: string[];
  required: boolean;
  usageCount: number;
  tier: CustomFieldTier;
  definition?: CustomFieldDefinition;
};

/**
 * All 4 current native fields (gender, language, metaTag, source) happen
 * to be valueType: 'select' — "Dropdown" is an accurate, simple label for
 * all of them today. If a future native field isn't select-shaped, this
 * needs a real valueType -> label map instead of a flat string.
 */
const NATIVE_FIELD_DATA_TYPE_LABEL = 'Dropdown';

const buildNativeRow = (field: FilterFieldConfig): CustomFieldTableRow => ({
  key: field.key,
  label: field.label,
  dataTypeLabel: NATIVE_FIELD_DATA_TYPE_LABEL,
  isFilterable: true,
  choices: (field.options ?? []).map((option) => option.label),
  required: false,
  usageCount: 0,
  tier: 'native',
});

const buildDefinitionRow = (definition: CustomFieldDefinition): CustomFieldTableRow => ({
  key: definition.Key ?? '',
  itemId: definition.ItemId,
  label: definition.Label ?? '',
  dataTypeLabel: getAttributeTypeLabel(definition),
  isFilterable: definition.IsFilterable !== false,
  choices: definition.Options ?? [],
  required: definition.Required === true,
  usageCount: Number(definition.UsageCount ?? 0),
  tier: definition.IsDefault ? 'default' : 'custom',
  definition,
});

export const formatAttributeUsage = (usageCount: number): string => {
  if (usageCount <= 0) return 'Not in use';
  return `${usageCount} contact${usageCount === 1 ? '' : 's'}`;
};

export const buildCustomFieldsTableRows = (
  nativeFields: FilterFieldConfig[],
  definitions: CustomFieldDefinition[]
): CustomFieldTableRow[] => {
  const nativeRows = nativeFields.map(buildNativeRow);
  const definitionRows = definitions.map(buildDefinitionRow);
  const defaultRows = definitionRows.filter((row) => row.tier === 'default');
  const customRows = definitionRows.filter((row) => row.tier === 'custom');

  return [...nativeRows, ...defaultRows, ...customRows];
};
